import React, { useCallback, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Grid, Container, Stack } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import cloneDeep from 'lodash/cloneDeep';

import { DialogContext } from '@cvt/contexts/DialogContext/DialogContext';
import { useRouter } from '@cvt/routing';

import { GiftForm } from '@modules/Gift/components/GiftForm';
import GiftHeader from '@modules/Gift/components/GiftHeader';
import { useGiftCrud } from '@modules/Gift/hooks/useGiftCrud';

import { useCheckoutSession } from '@modules/Payments/useCheckoutSession';
import GiftDetail from '@modules/Gift/components/GiftDetail';
import GiftTestimonial from '@modules/Gift/components/GiftTestimonial';

import { cacheKeys } from '@modules/Auth/config';
import { useCreditCrud } from '@modules/Credit/hooks/useCreditCrud';
import { useMe } from '@modules/Auth/hooks/useMe';
import { FormData } from '@modules/Gift/components/GiftForm';


interface Props {
  isRegift?: boolean;
}

const localStorageKey = `${import.meta.env.VITE__CVT_PROJECT_KEY}-gift-form-data`;

export const GiftView: React.FC<Props> = ({ isRegift }) => {
  const { creditId } = useParams<{ creditId: string }>();
  const { createGift } = useGiftCrud();
  const { editCredit } = useCreditCrud();
  const { createSession } = useCheckoutSession();
  const router = useRouter();
  const queryClient = useQueryClient();
  const giftPrice: string = import.meta.env.VITE__CVT_GIFT_PRICE_ID;
  const { openDialog, closeDialog, openedDialogs } = useContext(DialogContext);

  const [cachedFormSaveData, setCachedFormSaveData, removeCachedFormSaveData] = useLocalStorage<FormData | null>(localStorageKey, null);

  const { user } = useMe();

  const formDataRef = useRef<FormData | null>(null);

  const saveGift = useCallback(async (giftData: Gift.Crud) => {
    try {
      if (isRegift && creditId) {
        await editCredit.mutateAsync({
          ...giftData,
          id: parseInt(creditId, 10),
        });
        await queryClient.invalidateQueries({
          queryKey: [cacheKeys.getMe],
        });
        router.tributes.myTributes.go();
      } else {
        const { data: gift } = await createGift.mutateAsync(giftData);
        const checkoutPayload = {
          quantity: giftData.creditAmount,
          price_id: giftPrice,
          gift_id: gift.id,
        };

        const { url } = await createSession.mutateAsync(checkoutPayload);
        window.location.assign(url);
      }
    } catch (error) {
      console.error('Error saving gift:', error);
    }

  }, [createGift, createSession, isRegift, creditId, queryClient, router.tributes.myTributes, giftPrice, editCredit]);

  const onSubmit = useCallback(async (data: FormData) => {
    try {
      if (!user) {
        formDataRef.current = cloneDeep(data);
        setCachedFormSaveData(data);
        openDialog('authentication', {
          step: 'signup',
          onAuthenticationSuccess: async () => {
            if (formDataRef.current) {
              closeDialog('authentication');
              openDialog('creatingGift');
              await saveGift(formDataRef.current);
              removeCachedFormSaveData();
              closeDialog('creatingGift');

            }
          },
        });
      } else {
        openDialog('creatingGift');
        await saveGift(data);
        closeDialog('creatingGift');
        removeCachedFormSaveData();
      }
    } catch (error) {
      console.error('Error during submission:', error);
      closeDialog('creatingGift');
    }
  }, [user, setCachedFormSaveData, openDialog, closeDialog, saveGift, removeCachedFormSaveData]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Grid
        container
        spacing={4}
        justifyContent={isRegift ? 'center' : undefined}
        alignItems={isRegift ? 'center' : undefined}
      >
        <Grid size={12}>
          <GiftHeader/>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box>
            <GiftForm
              onSubmitRequest={onSubmit}
              isRegift={isRegift}
              defaultValues={{ ...(cachedFormSaveData || {}) }}
              slots={{
                footer: (formState) => (
                  <Box display="flex" justifyContent="flex-end">
                    <LoadingButton
                      loading={!!openedDialogs['creatingGift'] || !!openedDialogs['authentication']}
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                    >
                      {isRegift ? 'Gift the credit': 'Continue to Payment'}
                    </LoadingButton>
                  </Box>
                ),
              }}
            />
          </Box>
        </Grid>
        {!isRegift && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={3}>
              <GiftDetail/>
              <GiftTestimonial/>
            </Stack>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};
